import React, { useEffect, useRef, useState } from "react";
import BottomSheet from "../../../../components/ui/BottomSheet";
import SheetHeader from "../../../../components/ui/SheetHeader";
import Icon from "../../../../components/ui/Icon";
import { ICartProduct, IExtra, IProduct, IStep, IStepOption } from "../../../../classes/interfaces";
import { formatBRL } from "../../../../helpers/currency.helper";
import { calcFractionedProductTotal } from "../../../../utils/cartCalculations";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import { useFlavourExtras } from "../../hooks/useFlavourExtras";
import StepGroupHeader from "./StepGroupHeader";
import OptionRow from "./OptionRow";
import ChoiceOptionRow from "./ChoiceOptionRow";
import FlavorRow from "./FlavorRow";
import { stepSubtitle } from "./stepSubtitle";
import {
  Scroll, FlavorList, ObsSection, ObsLabel, ObsHint, Textarea, OptionsCard, ChoiceList,
  ExtrasIntro, FlavourCard, FlavourCardHead, FlavourCardTitle, FlavourCardMeta, FlavourCardBody,
  FlavourCardEmpty, Footer, ConfirmButton,
  SectionTitle, SummaryCard, SummaryLine, EditButton,
} from "./PizzaSheet.styles";

type PizzaSheetStage = "flavors" | "steps" | "extras";

interface LocalSize {
  id: string; name: string; groupName: string; fractioned: boolean;
  fractionalMaximum: number; selected: number; total: number; obs: string;
}

interface PizzaSheetProps {
  open: boolean;
  stage: PizzaSheetStage;
  localSize: LocalSize;
  productsSize: IProduct[];
  onMoreFlavor: (product: IProduct) => void;
  onLessFlavor: (product: IProduct) => void;
  onChangeSizeObs: (obs: string) => void;
  onConfirmFlavors: () => void;
  getPriceLeft: (product: IProduct) => JSX.Element;
  steps: IStep[];
  onMoreStepOption: (step: IStep, option: IStepOption) => void;
  onLessStepOption: (step: IStep, option: IStepOption) => void;
  onConfirmSteps: () => void;
  fractionedProduct: ICartProduct | null;
  onConfirmExtras: () => void;
  onEditFlavors: () => void;
  onEditSteps: () => void;
  onClose: () => void;
  /** Cardápio-vitrine (/menu): lista os sabores com preço, sem escolher nem avançar. */
  readOnly?: boolean;
}

function FlavourExtrasCard({
  flavour, amountFlavors, onChange,
}: { flavour: ICartProduct; amountFlavors: number; onChange: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const { extras, loading, moreExtra, lessExtra } = useFlavourExtras(flavour, amountFlavors, expanded, onChange);
  const count = flavour.extras.reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <FlavourCard>
      <FlavourCardHead type="button" onClick={() => setExpanded((v) => !v)}>
        <FlavourCardTitle>{flavour.product}</FlavourCardTitle>
        <FlavourCardMeta>{count ? `${count} adicional${count > 1 ? "is" : ""}` : "Sem adicionais"}</FlavourCardMeta>
        <Icon name={expanded ? "chevronDown" : "chevron"} size={18} color="#9AA8A3" />
      </FlavourCardHead>
      {expanded && (
        <FlavourCardBody>
          {loading && <FlavourCardEmpty>Carregando…</FlavourCardEmpty>}
          {!loading && (!extras || extras.length === 0) && (
            <FlavourCardEmpty>Produto não permite adicional</FlavourCardEmpty>
          )}
          {!loading &&
            extras &&
            extras.map((extra: IExtra) => (
              <OptionRow
                key={extra.id}
                name={extra.name}
                price={extra.price / amountFlavors}
                qty={extra.amount}
                max={extra.max > 0 ? extra.max : 99}
                onChange={(v) => (v > (extra.amount || 0) ? moreExtra(extra) : lessExtra(extra))}
              />
            ))}
        </FlavourCardBody>
      )}
    </FlavourCard>
  );
}

export default function PizzaSheet({
  open, stage, localSize, productsSize, onMoreFlavor, onLessFlavor, onChangeSizeObs, onConfirmFlavors, getPriceLeft,
  steps, onMoreStepOption, onLessStepOption, onConfirmSteps,
  fractionedProduct, onConfirmExtras,
  onEditFlavors, onEditSteps,
  onClose,
  readOnly = false,
}: PizzaSheetProps) {
  const desktop = useIsDesktopViewport();
  const [, forceRender] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeSectionRef = useRef<HTMLDivElement>(null);

  // Ao avançar/voltar de estágio, rola suavemente até a seção ativa — mesmo
  // padrão do StepProductSheet.
  useEffect(() => {
    if (!open) return;
    const el = activeSectionRef.current;
    const sc = scrollRef.current;
    if (el && sc && typeof sc.scrollTo === "function") {
      sc.scrollTo({
        top: el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 8,
        behavior: "smooth",
      });
    }
  }, [stage, open]);

  function recalcExtras() {
    if (fractionedProduct) {
      calcFractionedProductTotal(fractionedProduct);
      forceRender((n) => n + 1);
    }
  }

  // confirmSteps entrega o produto com extras de etapa (ex.: borda) ainda fora de
  // product.total — o ModalFracionados original recalculava no mount; sem isso o
  // total ao abrir (e o Adicionar) perde o valor das etapas.
  useEffect(() => {
    if (stage === "extras" && fractionedProduct) {
      calcFractionedProductTotal(fractionedProduct);
      forceRender((n) => n + 1);
    }
  }, [stage, fractionedProduct]);

  const title = `${localSize.groupName} ${localSize.name}`.trim();
  const flavourLimit = localSize.fractionalMaximum > 1 ? `até ${localSize.fractionalMaximum} sabores` : "1 sabor";
  const sub = readOnly
    ? `Monte com ${flavourLimit}`
    : `Escolha ${flavourLimit} · ${localSize.selected} de ${localSize.fractionalMaximum}`;

  const chosenFlavours = productsSize.filter((p) => (p.amount || 0) > 0);

  return (
    <BottomSheet open={open} onClose={onClose} maxHeight="94%" centered={desktop}>
      <SheetHeader title={title} sub={sub} onClose={onClose} />
      <Scroll ref={scrollRef}>
        {stage === "flavors" ? (
          <div ref={activeSectionRef}>
            <FlavorList>
              {productsSize.map((product) =>
                readOnly ? (
                  <FlavorRow
                    key={product.id}
                    name={product.name}
                    desc={product.description}
                    photo={product.imageUrl}
                    priceLabel={getPriceLeft(product)}
                    qty={0}
                    onChange={() => {}}
                    readOnly
                  />
                ) : localSize.fractioned ? (
                  <FlavorRow
                    key={product.id}
                    name={product.name}
                    desc={product.description}
                    photo={product.imageUrl}
                    priceLabel={getPriceLeft(product)}
                    qty={product.amount || 0}
                    max={localSize.fractionalMaximum}
                    disabledPlus={(product.amount || 0) === 0 && localSize.selected >= localSize.fractionalMaximum}
                    onChange={(v) => (v > (product.amount || 0) ? onMoreFlavor(product) : onLessFlavor(product))}
                  />
                ) : (
                  <ChoiceOptionRow
                    key={product.id}
                    name={product.name}
                    desc={product.description}
                    photo={product.imageUrl}
                    checked={(product.amount || 0) > 0}
                    onClick={() => onMoreFlavor(product)}
                  />
                )
              )}
            </FlavorList>
            {!readOnly && (
              <ObsSection>
                <ObsLabel>
                  Alguma observação? <ObsHint>opcional</ObsHint>
                </ObsLabel>
                <Textarea
                  defaultValue={localSize.obs}
                  onChange={(e) => onChangeSizeObs(e.target.value)}
                  placeholder="Ex.: cortar em 8 fatias…"
                  rows={2}
                />
              </ObsSection>
            )}
          </div>
        ) : (
          <div>
            <SectionTitle>Sabores</SectionTitle>
            <SummaryCard>
              {chosenFlavours.map((p) => (
                <SummaryLine key={p.id}>
                  <span>
                    {(p.amount || 0) > 1 ? `${p.amount}/${localSize.selected} ` : ""}
                    {p.name}
                  </span>
                </SummaryLine>
              ))}
              <EditButton type="button" onClick={onEditFlavors}>Alterar sabores</EditButton>
            </SummaryCard>
          </div>
        )}

        {stage === "steps" && (
          <div ref={activeSectionRef}>
            <SectionTitle>Monte seu pedido</SectionTitle>
            {steps.map((step, i) => (
              <div key={step.id}>
                <StepGroupHeader
                  index={i + 1}
                  title={step.name}
                  subtitle={stepSubtitle(step)}
                  required={step.minimum > 0}
                  done={step.selected >= step.minimum}
                />
                <div style={{ padding: "12px 0 16px" }}>
                  {step.maximum === 1 ? (
                    <ChoiceList>
                      {step.options.map((option) => (
                        <ChoiceOptionRow
                          key={option.id}
                          name={option.name}
                          price={option.price}
                          checked={option.amount === 1}
                          onClick={() => onMoreStepOption(step, option)}
                        />
                      ))}
                    </ChoiceList>
                  ) : (
                    <OptionsCard>
                      {step.options.map((option) => (
                        <OptionRow
                          key={option.id}
                          name={option.name}
                          price={option.price}
                          qty={option.amount}
                          max={option.maximum > 0 ? option.maximum : 9}
                          onChange={(v) => (v > (option.amount || 0) ? onMoreStepOption(step, option) : onLessStepOption(step, option))}
                        />
                      ))}
                    </OptionsCard>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {stage === "extras" && steps.length > 0 && (
          <div>
            <SectionTitle>Etapas</SectionTitle>
            <SummaryCard>
              {steps.flatMap((step) =>
                step.options
                  .filter((option) => option.amount > 0)
                  .map((option) => (
                    <SummaryLine key={option.id}>
                      <span>{option.amount}x {option.name}</span>
                      <span>{step.name}</span>
                    </SummaryLine>
                  ))
              )}
              {steps.every((step) => step.options.every((o) => !o.amount)) && (
                <SummaryLine><span>Nenhuma etapa selecionada</span></SummaryLine>
              )}
              <EditButton type="button" onClick={onEditSteps}>Alterar etapas</EditButton>
            </SummaryCard>
          </div>
        )}

        {stage === "extras" && fractionedProduct && (
          <div ref={activeSectionRef}>
            <ExtrasIntro>Deseja escolher adicional por sabor?</ExtrasIntro>
            {fractionedProduct.flavours!.map((flavour) => (
              <FlavourExtrasCard
                key={flavour.id}
                flavour={flavour}
                amountFlavors={fractionedProduct.flavours!.length}
                onChange={recalcExtras}
              />
            ))}
          </div>
        )}
      </Scroll>

      {!readOnly && (
        <Footer>
          {stage === "flavors" && (
            <ConfirmButton disabled={localSize.selected === 0} onClick={onConfirmFlavors}>
              {localSize.selected === 0 ? "Escolha ao menos 1 sabor" : `Continuar • ${formatBRL(localSize.total)}`}
            </ConfirmButton>
          )}
          {stage === "steps" && <ConfirmButton onClick={onConfirmSteps}>Confirmar</ConfirmButton>}
          {stage === "extras" && (
            <ConfirmButton onClick={onConfirmExtras}>
              Adicionar Item {formatBRL(fractionedProduct?.total || 0)}
            </ConfirmButton>
          )}
        </Footer>
      )}
    </BottomSheet>
  );
}
